import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Alert,
  InputGroup,
  Spinner,
  Tab,
  Tabs,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Play,
  Pause,
  Calendar,
  Target,
  Tag,
  Package,
  Percent,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import promotionService from "../../services/promotionService";
import productService from "../../services/productService";

const PromotionManagement = () => {
  const { user } = useAuth();

  // États principaux
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // États des filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // États des modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  // États du formulaire
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    promotionType: "PERIOD",
    startDate: "",
    endDate: "",
    remainingSales: 1,
    applyToAll: false,
    discountPercentage: "",
    discountAmount: "",
    products: [],
    activateNow: false,
  });

  const [formErrors, setFormErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Pour la recherche de produits dans le formulaire
  const [productSearchTerm, setProductSearchTerm] = useState("");

  // Charger les données au montage
  useEffect(() => {
    loadPromotions();
    loadProducts();
  }, []);

  // Charger les promotions depuis l'API
  const loadPromotions = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📋 Chargement des promotions...');
      const response = await promotionService.getAllPromotions();

      if (response.success) {
        setPromotions(response.data || []);
        console.log(`✅ ${response.data?.length || 0} promotion(s) chargée(s)`);
      } else {
        setError(response.message || 'Erreur lors du chargement');
        setPromotions([]);
      }
    } catch (err) {
      console.error("❌ Erreur chargement promotions:", err);
      setError("Erreur lors du chargement des promotions");
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger les produits depuis l'API
  const loadProducts = async () => {
    try {
      console.log('📦 Chargement des produits...');
      const response = await productService.getProducts({ size: 500 });
      
      if (response.success) {
        let productsData = [];
        
        if (Array.isArray(response.data)) {
          productsData = response.data;
        } else if (response.data?.content) {
          productsData = response.data.content;
        }
        
        // Filtrer les produits actifs
        const activeProducts = productsData.filter(p => p.isActive !== false);
        setProducts(activeProducts);
        console.log(`✅ ${activeProducts.length} produit(s) chargé(s)`);
      } else {
        console.warn('⚠️ Impossible de charger les produits');
        setProducts([]);
      }
    } catch (err) {
      console.error("❌ Erreur chargement produits:", err);
      setProducts([]);
    }
  };

  // Filtrage des promotions
  const getFilteredPromotions = useCallback(() => {
    return promotions.filter((promo) => {
      const matchesSearch =
        promo.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        promo.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !statusFilter || promo.status === statusFilter;
      const matchesType = !typeFilter || promo.promotionType === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [promotions, searchTerm, statusFilter, typeFilter]);

  // Filtrage des produits pour le formulaire
  const getFilteredProducts = useCallback(() => {
    if (!productSearchTerm.trim()) return products;
    
    const term = productSearchTerm.toLowerCase();
    return products.filter(p => 
      p.name?.toLowerCase().includes(term) ||
      p.sku?.toLowerCase().includes(term) ||
      p.barcode?.toLowerCase().includes(term) ||
      p.categoryName?.toLowerCase().includes(term)
    );
  }, [products, productSearchTerm]);

  // Handlers du formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddProduct = (product) => {
    if (formData.products.find((p) => p.productId === product.id)) {
      return; // Déjà ajouté
    }

    const promoProduct = {
      productId: product.id,
      productName: product.name,
      originalPrice: product.price,
      promoPrice: product.price,
      discountPercentage: 0,
    };

    setFormData((prev) => ({
      ...prev,
      products: [...prev.products, promoProduct],
    }));
    
    setProductSearchTerm(""); // Réinitialiser la recherche
  };

  const handleRemoveProduct = (productId) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.filter((p) => p.productId !== productId),
    }));
  };

  const handleProductPriceChange = (productId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.map((p) => {
        if (p.productId !== productId) return p;

        const updated = { ...p };

        if (field === "promoPrice") {
          updated.promoPrice = parseFloat(value) || 0;
          if (p.originalPrice > 0) {
            updated.discountPercentage = Math.round(
              ((p.originalPrice - updated.promoPrice) / p.originalPrice) * 100
            );
          }
        } else if (field === "discountPercentage") {
          updated.discountPercentage = parseFloat(value) || 0;
          updated.promoPrice = Math.round(
            p.originalPrice * (1 - updated.discountPercentage / 100)
          );
        }

        return updated;
      }),
    }));
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      promotionType: "PERIOD",
      startDate: "",
      endDate: "",
      remainingSales: 1,
      applyToAll: false,
      discountPercentage: "",
      discountAmount: "",
      products: [],
      activateNow: false,
    });
    setFormErrors([]);
    setProductSearchTerm("");
  };

  // Ouvrir le modal de création
  const handleOpenCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  // Ouvrir le modal d'édition
  const handleOpenEditModal = (promotion) => {
    setSelectedPromotion(promotion);
    
    // Formater les produits pour le formulaire
    const formattedProducts = (promotion.products || []).map(p => ({
      productId: p.productId,
      productName: p.productName,
      originalPrice: p.originalPrice,
      promoPrice: p.promoPrice,
      discountPercentage: p.discountPercentage || 0
    }));
    
    setFormData({
      name: promotion.name || "",
      description: promotion.description || "",
      promotionType: promotion.promotionType || "PERIOD",
      startDate: promotion.startDate ? promotion.startDate.substring(0, 16) : "",
      endDate: promotion.endDate ? promotion.endDate.substring(0, 16) : "",
      remainingSales: promotion.remainingSales || 1,
      applyToAll: promotion.applyToAll || false,
      discountPercentage: promotion.discountPercentage || "",
      discountAmount: promotion.discountAmount || "",
      products: formattedProducts,
      activateNow: false,
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  // Valider le formulaire
  const validateForm = () => {
    const errors = [];

    if (!formData.name || formData.name.trim().length < 2) {
      errors.push("Le nom doit contenir au moins 2 caractères");
    }

    if (formData.promotionType === "PERIOD") {
      if (formData.endDate && new Date(formData.endDate) < new Date()) {
        errors.push("La date de fin ne peut pas être dans le passé");
      }
      if (formData.startDate && formData.endDate && 
          new Date(formData.endDate) < new Date(formData.startDate)) {
        errors.push("La date de fin doit être après la date de début");
      }
    }

    if (!formData.applyToAll && formData.products.length === 0) {
      errors.push("Ajoutez au moins un produit ou activez 'Appliquer à tous'");
    }

    if (formData.applyToAll && !formData.discountPercentage && !formData.discountAmount) {
      errors.push("Définissez un pourcentage ou un montant de réduction");
    }

    // Vérifier les prix promo
    for (const product of formData.products) {
      if (product.promoPrice >= product.originalPrice) {
        errors.push(`Le prix promo de "${product.productName}" doit être inférieur au prix original`);
      }
      if (product.promoPrice <= 0) {
        errors.push(`Le prix promo de "${product.productName}" doit être supérieur à 0`);
      }
    }

    return errors;
  };

  // Créer une promotion
  const handleCreatePromotion = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSaving(true);
      setFormErrors([]);

      const response = await promotionService.createPromotion(formData);

      if (response.success) {
        setSuccess("Promotion créée avec succès");
        setShowCreateModal(false);
        resetForm();
        loadPromotions();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setFormErrors([response.message || "Erreur lors de la création"]);
      }
    } catch (err) {
      setFormErrors([err.message || "Erreur lors de la création"]);
    } finally {
      setSaving(false);
    }
  };

  // Mettre à jour une promotion
  const handleUpdatePromotion = async () => {
    if (!selectedPromotion) return;

    const errors = validateForm();
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSaving(true);
      setFormErrors([]);

      const response = await promotionService.updatePromotion(
        selectedPromotion.id,
        formData
      );

      if (response.success) {
        setSuccess("Promotion mise à jour");
        setShowEditModal(false);
        loadPromotions();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setFormErrors([response.message || "Erreur lors de la mise à jour"]);
      }
    } catch (err) {
      setFormErrors([err.message || "Erreur lors de la mise à jour"]);
    } finally {
      setSaving(false);
    }
  };

  // Supprimer une promotion
  const handleDeletePromotion = async () => {
    if (!selectedPromotion) return;

    try {
      setSaving(true);

      const response = await promotionService.deletePromotion(selectedPromotion.id);

      if (response.success) {
        setSuccess("Promotion supprimée");
        setShowDeleteModal(false);
        setSelectedPromotion(null);
        loadPromotions();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || "Erreur lors de la suppression");
      }
    } catch (err) {
      setError(err.message || "Erreur lors de la suppression");
    } finally {
      setSaving(false);
    }
  };

  // Activer/Désactiver une promotion
  const handleToggleStatus = async (promotion) => {
    try {
      let response;

      if (promotion.status === "ACTIVE") {
        response = await promotionService.deactivatePromotion(promotion.id);
      } else {
        response = await promotionService.activatePromotion(promotion.id);
      }

      if (response.success) {
        setSuccess(
          promotion.status === "ACTIVE"
            ? "Promotion désactivée"
            : "Promotion activée"
        );
        loadPromotions();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || "Erreur lors de l'opération");
      }
    } catch (err) {
      setError(err.message || "Erreur lors de l'opération");
    }
  };

  // Afficher les détails
  const handleViewDetails = async (promotion) => {
    try {
      // Charger les détails complets
      const response = await promotionService.getPromotionById(promotion.id);
      if (response.success) {
        setSelectedPromotion(response.data);
      } else {
        setSelectedPromotion(promotion);
      }
    } catch (err) {
      setSelectedPromotion(promotion);
    }
    setShowDetailModal(true);
  };

  // Formater le prix
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "0 FCFA";
    return new Intl.NumberFormat("fr-FR", {
      style: "decimal",
      minimumFractionDigits: 0,
    }).format(amount) + " FCFA";
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "Non définie";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Obtenir le badge de statut
  const getStatusBadge = (status) => {
    const config = {
      DRAFT: { bg: "secondary", icon: <Edit size={12} />, label: "Brouillon" },
      ACTIVE: { bg: "success", icon: <CheckCircle size={12} />, label: "Active" },
      INACTIVE: { bg: "warning", icon: <Pause size={12} />, label: "Inactive" },
      EXPIRED: { bg: "danger", icon: <XCircle size={12} />, label: "Expirée" },
      COMPLETED: { bg: "info", icon: <CheckCircle size={12} />, label: "Terminée" },
    };

    const cfg = config[status] || config.DRAFT;

    return (
      <Badge bg={cfg.bg} className="d-flex align-items-center gap-1">
        {cfg.icon} {cfg.label}
      </Badge>
    );
  };

  // Obtenir le badge de type
  const getTypeBadge = (type) => {
    if (type === "PERIOD") {
      return (
        <Badge bg="primary" className="d-flex align-items-center gap-1">
          <Calendar size={12} /> Période
        </Badge>
      );
    }
    return (
      <Badge bg="info" className="d-flex align-items-center gap-1">
        <Target size={12} /> Prochaine vente
      </Badge>
    );
  };

  const filteredPromotions = getFilteredPromotions();
  const filteredProducts = getFilteredProducts();

  // Statistiques
  const stats = {
    total: promotions.length,
    active: promotions.filter(p => p.status === "ACTIVE").length,
    productsInPromo: promotions.reduce((sum, p) => sum + (p.productCount || 0), 0),
    nextSale: promotions.filter(p => p.promotionType === "NEXT_SALE").length
  };

  return (
    <Container fluid className="py-4">
      {/* Messages */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <AlertCircle size={18} className="me-2" />
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <CheckCircle size={18} className="me-2" />
          {success}
        </Alert>
      )}

      {/* En-tête */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <h2 className="fw-bold mb-1 d-flex align-items-center">
                <Tag size={28} className="me-2 text-primary" />
                Gestion des Promotions
              </h2>
              <p className="text-muted mb-0">
                Configurez les prix promotionnels de vos produits
              </p>
            </div>
            <Button variant="success" onClick={handleOpenCreateModal}>
              <Plus size={18} className="me-2" />
              Nouvelle Promotion
            </Button>
          </div>
        </Col>
      </Row>

      {/* Statistiques rapides */}
      <Row className="mb-4 g-3">
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                <Tag size={24} className="text-primary" />
              </div>
              <div>
                <h4 className="mb-0">{stats.total}</h4>
                <small className="text-muted">Total promotions</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                <CheckCircle size={24} className="text-success" />
              </div>
              <div>
                <h4 className="mb-0">{stats.active}</h4>
                <small className="text-muted">Actives</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                <Package size={24} className="text-info" />
              </div>
              <div>
                <h4 className="mb-0">{stats.productsInPromo}</h4>
                <small className="text-muted">Produits en promo</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-warning bg-opacity-10 p-3 me-3">
                <Target size={24} className="text-warning" />
              </div>
              <div>
                <h4 className="mb-0">{stats.nextSale}</h4>
                <small className="text-muted">Prochaine vente</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={4}>
                  <Form.Label className="small text-muted">Rechercher</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Nom de la promotion..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Label className="small text-muted">Statut</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="DRAFT">Brouillon</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="EXPIRED">Expirée</option>
                    <option value="COMPLETED">Terminée</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="small text-muted">Type</Form.Label>
                  <Form.Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="">Tous les types</option>
                    <option value="PERIOD">Période</option>
                    <option value="NEXT_SALE">Prochaine vente</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button
                    variant="outline-secondary"
                    className="w-100"
                    onClick={loadPromotions}
                    disabled={loading}
                  >
                    <RefreshCw size={16} className={loading ? "spin" : ""} />
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Liste des promotions */}
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Chargement...</p>
                </div>
              ) : filteredPromotions.length === 0 ? (
                <div className="text-center py-5">
                  <Tag size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">Aucune promotion</h5>
                  <p className="text-muted mb-4">
                    {promotions.length === 0
                      ? "Créez votre première promotion"
                      : "Aucune promotion ne correspond à vos critères"}
                  </p>
                  <Button variant="success" onClick={handleOpenCreateModal}>
                    <Plus size={16} className="me-2" />
                    Créer une promotion
                  </Button>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Promotion</th>
                      <th>Type</th>
                      <th>Statut</th>
                      <th>Période / Ventes</th>
                      <th>Produits</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPromotions.map((promo) => (
                      <tr key={promo.id}>
                        <td>
                          <div>
                            <strong>{promo.name}</strong>
                            {promo.description && (
                              <div className="small text-muted text-truncate" style={{ maxWidth: 200 }}>
                                {promo.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{getTypeBadge(promo.promotionType)}</td>
                        <td>{getStatusBadge(promo.status)}</td>
                        <td>
                          {promo.promotionType === "PERIOD" ? (
                            <div className="small">
                              <div>
                                <Calendar size={12} className="me-1" />
                                {formatDate(promo.startDate)}
                              </div>
                              <div className="text-muted">
                                → {formatDate(promo.endDate)}
                              </div>
                            </div>
                          ) : (
                            <div className="small">
                              <Target size={12} className="me-1" />
                              {promo.remainingSales || 1} vente(s) restante(s)
                            </div>
                          )}
                        </td>
                        <td>
                          <Badge bg="secondary">
                            {promo.applyToAll
                              ? "Tous les produits"
                              : `${promo.productCount || 0} produit(s)`}
                          </Badge>
                          {promo.discountPercentage > 0 && (
                            <div className="small text-success">
                              -{promo.discountPercentage}%
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="d-flex justify-content-center gap-1">
                            <OverlayTrigger overlay={<Tooltip>Voir détails</Tooltip>}>
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => handleViewDetails(promo)}
                              >
                                <Eye size={14} />
                              </Button>
                            </OverlayTrigger>

                            {promo.status !== "COMPLETED" && promo.status !== "EXPIRED" && (
                              <>
                                <OverlayTrigger
                                  overlay={
                                    <Tooltip>
                                      {promo.status === "ACTIVE" ? "Désactiver" : "Activer"}
                                    </Tooltip>
                                  }
                                >
                                  <Button
                                    variant={promo.status === "ACTIVE" ? "outline-warning" : "outline-success"}
                                    size="sm"
                                    onClick={() => handleToggleStatus(promo)}
                                  >
                                    {promo.status === "ACTIVE" ? <Pause size={14} /> : <Play size={14} />}
                                  </Button>
                                </OverlayTrigger>

                                <OverlayTrigger overlay={<Tooltip>Modifier</Tooltip>}>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleOpenEditModal(promo)}
                                  >
                                    <Edit size={14} />
                                  </Button>
                                </OverlayTrigger>
                              </>
                            )}

                            <OverlayTrigger overlay={<Tooltip>Supprimer</Tooltip>}>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setSelectedPromotion(promo);
                                  setShowDeleteModal(true);
                                }}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal Création/Édition */}
      <Modal
        show={showCreateModal || showEditModal}
        onHide={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <Tag size={20} className="me-2" />
            {showEditModal ? "Modifier la promotion" : "Nouvelle promotion"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {formErrors.length > 0 && (
            <Alert variant="danger">
              <ul className="mb-0">
                {formErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </Alert>
          )}

          <Tabs defaultActiveKey="general" className="mb-3">
            {/* Onglet Général */}
            <Tab eventKey="general" title="Général">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Nom de la promotion *</Form.Label>
                    <Form.Control
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Ex: Soldes d'été"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Type de promotion *</Form.Label>
                    <Form.Select
                      name="promotionType"
                      value={formData.promotionType}
                      onChange={handleInputChange}
                    >
                      <option value="PERIOD">📅 Période définie</option>
                      <option value="NEXT_SALE">🎯 Prochaine vente uniquement</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Description optionnelle..."
                    />
                  </Form.Group>
                </Col>

                {formData.promotionType === "PERIOD" && (
                  <>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Date de début</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Date de fin</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          name="endDate"
                          value={formData.endDate}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}

                {formData.promotionType === "NEXT_SALE" && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Nombre de ventes</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        name="remainingSales"
                        value={formData.remainingSales}
                        onChange={handleInputChange}
                      />
                      <Form.Text className="text-muted">
                        La promo se désactivera après ce nombre de ventes
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}

                <Col md={12}>
                  <Form.Check
                    type="checkbox"
                    id="activateNow"
                    name="activateNow"
                    label="Activer immédiatement après création"
                    checked={formData.activateNow}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>
            </Tab>

            {/* Onglet Produits */}
            <Tab eventKey="products" title="Produits">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Check
                    type="checkbox"
                    id="applyToAll"
                    name="applyToAll"
                    label="Appliquer à tous les produits de la boutique"
                    checked={formData.applyToAll}
                    onChange={handleInputChange}
                  />
                </Col>

                {formData.applyToAll ? (
                  <>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Réduction en %</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            min="0"
                            max="100"
                            name="discountPercentage"
                            value={formData.discountPercentage}
                            onChange={handleInputChange}
                            placeholder="Ex: 10"
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Ou réduction en montant</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            min="0"
                            name="discountAmount"
                            value={formData.discountAmount}
                            onChange={handleInputChange}
                            placeholder="Ex: 500"
                          />
                          <InputGroup.Text>FCFA</InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </>
                ) : (
                  <>
                    {/* Recherche et sélection de produits */}
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Rechercher un produit</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <Search size={16} />
                          </InputGroup.Text>
                          <Form.Control
                            placeholder="Rechercher par nom, SKU ou code-barres..."
                            value={productSearchTerm}
                            onChange={(e) => setProductSearchTerm(e.target.value)}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    {/* Liste des produits filtrés */}
                    {productSearchTerm && (
                      <Col md={12}>
                        <div className="border rounded" style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {filteredProducts.length === 0 ? (
                            <div className="p-3 text-center text-muted">
                              Aucun produit trouvé
                            </div>
                          ) : (
                            filteredProducts.slice(0, 10).map(product => (
                              <div 
                                key={product.id}
                                className="d-flex justify-content-between align-items-center p-2 border-bottom hover-bg"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleAddProduct(product)}
                              >
                                <div>
                                  <strong>{product.name}</strong>
                                  <div className="small text-muted">
                                    {product.categoryName} - {product.sku || 'Pas de SKU'}
                                  </div>
                                </div>
                                <div className="text-end">
                                  <div>{formatCurrency(product.price)}</div>
                                  <Button variant="outline-success" size="sm">
                                    <Plus size={12} />
                                  </Button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </Col>
                    )}

                    {/* Liste des produits ajoutés */}
                    {formData.products.length > 0 && (
                      <Col md={12}>
                        <h6 className="mt-3">Produits sélectionnés ({formData.products.length})</h6>
                        <Table size="sm" bordered>
                          <thead className="bg-light">
                            <tr>
                              <th>Produit</th>
                              <th>Prix original</th>
                              <th>Prix promo</th>
                              <th>Réduction %</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.products.map((product) => (
                              <tr key={product.productId}>
                                <td>{product.productName}</td>
                                <td>{formatCurrency(product.originalPrice)}</td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    min="0"
                                    value={product.promoPrice}
                                    onChange={(e) =>
                                      handleProductPriceChange(
                                        product.productId,
                                        "promoPrice",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <InputGroup size="sm">
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={Math.round(product.discountPercentage)}
                                      onChange={(e) =>
                                        handleProductPriceChange(
                                          product.productId,
                                          "discountPercentage",
                                          e.target.value
                                        )
                                      }
                                    />
                                    <InputGroup.Text>%</InputGroup.Text>
                                  </InputGroup>
                                </td>
                                <td className="text-center">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleRemoveProduct(product.productId)}
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                    )}
                  </>
                )}
              </Row>
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
              resetForm();
            }}
          >
            Annuler
          </Button>
          <Button
            variant="success"
            onClick={showEditModal ? handleUpdatePromotion : handleCreatePromotion}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Enregistrement...
              </>
            ) : showEditModal ? (
              "Mettre à jour"
            ) : (
              "Créer la promotion"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de suppression */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <Trash2 size={20} className="me-2" />
            Supprimer la promotion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <AlertCircle size={18} className="me-2" />
            Cette action est irréversible !
          </Alert>
          <p>
            Êtes-vous sûr de vouloir supprimer la promotion{" "}
            <strong>{selectedPromotion?.name}</strong> ?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDeletePromotion} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Supprimer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de détails */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-info text-white">
          <Modal.Title>
            <Eye size={20} className="me-2" />
            Détails de la promotion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPromotion && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <h5>{selectedPromotion.name}</h5>
                  <p className="text-muted">{selectedPromotion.description || "Pas de description"}</p>
                </Col>
                <Col md={6} className="text-md-end">
                  {getStatusBadge(selectedPromotion.status)}
                  <div className="mt-2">
                    {getTypeBadge(selectedPromotion.promotionType)}
                  </div>
                </Col>
              </Row>

              <Row className="mb-4 g-3">
                <Col md={4}>
                  <Card className="bg-light border-0">
                    <Card.Body className="text-center">
                      <Package size={24} className="text-primary mb-2" />
                      <h4 className="mb-0">
                        {selectedPromotion.applyToAll
                          ? "Tous"
                          : selectedPromotion.productCount || 0}
                      </h4>
                      <small className="text-muted">Produits</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="bg-light border-0">
                    <Card.Body className="text-center">
                      <Percent size={24} className="text-success mb-2" />
                      <h4 className="mb-0">
                        {selectedPromotion.discountPercentage || 0}%
                      </h4>
                      <small className="text-muted">Réduction moyenne</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="bg-light border-0">
                    <Card.Body className="text-center">
                      {selectedPromotion.promotionType === "PERIOD" ? (
                        <>
                          <Calendar size={24} className="text-info mb-2" />
                          <h6 className="mb-0">{formatDate(selectedPromotion.endDate)}</h6>
                          <small className="text-muted">Date de fin</small>
                        </>
                      ) : (
                        <>
                          <Target size={24} className="text-warning mb-2" />
                          <h4 className="mb-0">{selectedPromotion.remainingSales || 0}</h4>
                          <small className="text-muted">Ventes restantes</small>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Liste des produits */}
              {selectedPromotion.products && selectedPromotion.products.length > 0 && (
                <>
                  <h6 className="mb-3">Produits en promotion</h6>
                  <Table size="sm" bordered>
                    <thead className="bg-light">
                      <tr>
                        <th>Produit</th>
                        <th className="text-end">Prix original</th>
                        <th className="text-end">Prix promo</th>
                        <th className="text-end">Réduction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPromotion.products.map((p) => (
                        <tr key={p.id || p.productId}>
                          <td>{p.productName}</td>
                          <td className="text-end text-muted text-decoration-line-through">
                            {formatCurrency(p.originalPrice)}
                          </td>
                          <td className="text-end text-success fw-bold">
                            {formatCurrency(p.promoPrice)}
                          </td>
                          <td className="text-end">
                            <Badge bg="danger">-{Math.round(p.discountPercentage || 0)}%</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CSS */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .hover-bg:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </Container>
  );
};

export default PromotionManagement;